import axios from '@api';
import {
  transformFlow,
  transformTopic,
  transformCreateFastTrackResponse,
  transformFastTrackItemsData,
  transformFastTrackType,
  transformFastTrackPrompts,
} from './utilities/helpers';

/**
 * read flow
 * @param {Object} options
 * @param  {Number} options.id
 * @param  {Number}  options.signal
 * @return {Promise}
 */
export const readFlow = async ({ id, signal }) => {
  return axios({
    method: 'get',
    signal,
    url: `/api/flows/${id}`,
    withCredentials: true,
  }).then(({ data: flow }) => transformFlow(flow));
};

/**
 * ready flow topic
 * @param {Object} options
 * @param  {Number} options.id
 * @param {AbortSignal} [options.signal] AbortController signal
 * @return {Promise}
 */
export const readTopic = async ({ id, signal }) => {
  return axios({
    method: 'get',
    signal,
    url: `/api/flows/flowtopics/${id}`,
    withCredentials: true,
  }).then(({ data: topic }) => transformTopic(topic));
};

/**
 * read fast track list V2
 * @param  {Number} options.folderId
 * @param  {String} options.order   "asc" || "desc" || undefined
 * @param  {Number} options.page
 * @param  {String} options.query
 * @param  {Number} options.signal  AbortController signal
 * @param  {Number} options.size
 * @param  {String} options.sortBy
 * @param  {Array}  options.tags    [is_shared]
 * @return {Promise}
 */
export const readFastTrackItems = ({ order, page, query, signal, size, sortBy, tags }) => {
  return axios({
    method: 'get',
    params: {
      order_by: sortBy ? order : undefined,
      page,
      query: query?.length > 0 ? query : undefined,
      size,
      sort_by: sortBy,
      tags: tags?.length > 0 ? tags?.join(',') : undefined,
    },
    signal,
    transformResponse: [(data) => transformFastTrackItemsData(JSON.parse(data))],
    url: '/v2/api/flows',
    withCredentials: true,
  });
};

/**
 * update fast track name
 * @param  {Number} options.id
 * @param  {String} options.name
 * @return {Promise}
 */
export const updateFastTrackName = ({ id, name }) => {
  return axios({
    data: { name },
    method: 'put',
    url: `/v2/api/flows/${id}/rename`,
    withCredentials: true,
  });
};

/**
 * Update topic visibility
 * @param {Object} options
 * @param {Number} options.id
 * @param {boolean} options.isHidden
 * @return {Promise}
 */
export const updateTopicVisibility = async ({ id, isHidden }) => {
  return axios({
    data: { is_hidden: isHidden },
    method: 'put',
    url: `/v2/api/flows/flowtopics/${id}/hide`,
    withCredentials: true,
  });
};

/**
 * shares fast track with all users under a client
 * @param  {Number} options.clientId
 * @param  {Number} options.ids
 * @return {Promise}
 */
export const shareFastTrackWithClientGroup = ({ clientId, ids }) => {
  return axios({
    data: {
      flow_ids: ids,
      recipient_client_id: clientId,
    },
    method: 'post',
    url: '/v2/api/flows/client_share',
    withCredentials: true,
  });
};

/**
 * shares fast track with users
 * @param  {Array} options.ids
 * @param  {Boolean} options.skipEmail
 * @param  {Number} options.userIds
 * @return {Promise}
 */
export const shareFastTrackWithUsers = ({ ids, skipEmail = false, userIds }) => {
  return axios({
    data: {
      flow_ids: ids,
      recipient_ids: userIds,
      skip_email: skipEmail,
    },
    method: 'post',
    url: '/v2/api/flows/user_share',
    withCredentials: true,
  });
};

/**
 * submit feedback for a fast track
 * @param  {Number} options.feedbackId
 * @param  {Boolean} options.like
 * @param  {String} options.reason
 * @param  {String} options.reasonExplanation
 * @return {Promise}
 */
export const submitFastTrackFeedback = ({ feedbackId, like, reason, reasonExplanation }) => {
  return axios({
    data: {
      like,
      reason,
      reason_explanation: reason === 'other' ? reasonExplanation : null,
    },
    method: 'post',
    url: `/v2/api/flows/flowtopics/${feedbackId}/feedback`,
    withCredentials: true,
  });
};

/**
 * deletes fast tracks
 * @param {Array} options.ids
 * @return {Promise}
 */
export const deleteFastTracks = ({ ids }) => {
  return axios({
    data: {
      flow_ids: ids,
    },
    method: 'delete',
    url: '/v2/api/flows',
    withCredentials: true,
  });
};

/**
 * An api the returns data about an individual fast track
 * @function
 * @param {Function} transformFunction - Function to transform the fetched data (e.g., transformFastTrackType or transformFastTrackPrompts).
 * @returns {Function} - A function that takes an object with { fastTrackId, type } and returns a Promise resolving to the transformed data.
 */
const readFastTrackTypeCustomFunction = (transformFunction) => {
  return ({ fastTrackId, flowId }) => {
    return axios({
      method: 'get',
      params: { flow_id: flowId },
      url: `/api/narratives/${fastTrackId}/`,
      withCredentials: true,
    }).then(({ data }) => transformFunction(data));
  };
};

/**
 * Reads and transforms All Information about a FastTrack type from api
 *
 * @function
 * @param {String} fastTrackId i.e. brand-defense
 * @param {Number} [flowId] i.e. 123
 * @returns {Promise<Object>} Promise resolving to the transformed FastTrack data
 */
export const readFastTrackType = readFastTrackTypeCustomFunction(transformFastTrackType);

/**
 * Reads and transforms FastTrack to just it's prompts for use in ItemAnswers component
 *
 * @function
 * @param {String} fastTrackId i.e. brand-defense
 * @returns {Promise<Object>} Promise resolving to the transformed FastTrack prompts data.
 */
export const readFastTrackPrompts = readFastTrackTypeCustomFunction(transformFastTrackPrompts);

/**
 * @param {Object} options
 * @param {String} options.id
 * @param {PromptAnswer[]} options.promptAnswers
 * @param {String} options.title
 * @return {Promise}
 */
export const createFastTrack = async ({ id, promptAnswers, title }) => {
  return axios({
    data: {
      answers: promptAnswers,
      name: title,
    },
    method: 'post',
    url: `/api/narratives/${id}`,
    withCredentials: true,
  }).then(({ data }) => transformCreateFastTrackResponse(data));
};

/**
 * Get all fast track types
 * @return {Promise}
 */
export const readFastTracks = () => {
  return axios({
    method: 'get',
    url: '/v2/api/narratives',
    withCredentials: true,
  }).then(({ data }) => data);
};
